"use client";

import { Button, Input, Link, Select } from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import {
  ResourceFeedback,
  type ChartState,
} from "@softmax/web-ux/resource-feedback";
import * as stylex from "@stylexjs/stylex";
import type { CreatorCoworldRow } from "./coworld-records";
import { coworldLayout } from "./coworld-layout.styles";
import { CoworldLibraryTable } from "./coworld-library-table";

export function CoworldLibrary({
  uploadGuideHref,
  state,
  query,
  visibility,
  onQueryChange,
  onVisibilityChange,
  onRefresh,
  hasMore,
  loadingMore,
  onMore,
}: Readonly<{
  state: ChartState<Readonly<{ rows: readonly CreatorCoworldRow[] }>>;
  uploadGuideHref: string;
  query: string;
  visibility: string;
  onQueryChange(value: string): void;
  onVisibilityChange(value: string): void;
  onRefresh(): void;
  hasMore: boolean;
  loadingMore: boolean;
  onMore(): void;
}>) {
  const filtered = Boolean(query) || visibility !== "all";
  return (
    <div {...stylex.props(coworldLayout.stack)}>
      <div {...stylex.props(styles.toolbar)}>
        <div {...stylex.props(coworldLayout.inline)}>
          <div {...stylex.props(styles.search)}>
            <Input
              size="sm"
              type="search"
              aria-label="Search your Coworlds"
              placeholder="Find a Coworld…"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
            />
          </div>
          <Select
            aria-label="Visibility"
            value={visibility}
            onValueChange={onVisibilityChange}
            options={[
              { value: "all", label: "All visibility" },
              { value: "private", label: "Private" },
              { value: "public", label: "Public" },
            ]}
          />
        </div>
        <Button
          size="sm"
          variant="quiet"
          aria-label="Refresh Coworlds"
          aria-disabled={
            state.status === "loading" ||
            (state.status === "ready" && state.refresh?.status === "refreshing")
          }
          onClick={onRefresh}
        >
          ↻ Refresh
        </Button>
      </div>
      <ResourceFeedback
        state={state}
        subject="your Coworlds"
        onRefresh={onRefresh}
      />
      {state.status === "ready" &&
        (state.rows.length === 0 ? (
          <div {...stylex.props(coworldLayout.empty)}>
            <h2 {...stylex.props(coworldLayout.heading)}>
              {filtered ? "No matching Coworlds" : "Your Coworlds start here"}
            </h2>
            <p {...stylex.props(coworldLayout.muted)}>
              {filtered
                ? "Try another name or visibility."
                : "Upload a Coworld with the CLI to get started."}
            </p>
            <Link href={uploadGuideHref} focus="native">
              Build and upload a Coworld ↗
            </Link>
          </div>
        ) : (
          <CoworldLibraryTable rows={state.rows} />
        ))}
      {hasMore && (
        <Button size="sm" disabled={loadingMore} onClick={onMore}>
          {loadingMore ? "Loading…" : "Load more Coworlds"}
        </Button>
      )}
    </div>
  );
}
const styles = stylex.create({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.space12,
    flexWrap: "wrap",
    paddingBottom: spacing.space12,
  },
  search: { width: 280, maxWidth: "100%" },
});
